import React, { useState, useEffect } from 'react';
import goldColors from '../themes/goldColors';
import {
  AppBar,
  Toolbar,
  Box,
  Button,
  Container,
  Typography,
  IconButton,
  Menu,
  MenuItem,
  Drawer,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Divider,
  useMediaQuery,
  useTheme,
  Tooltip,
  Fade,
  Paper
} from '@mui/material';
import { Link as RouterLink, useLocation, useNavigate } from 'react-router-dom';
import MenuIcon from '@mui/icons-material/Menu';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import LanguageIcon from '@mui/icons-material/Language';
import DashboardIcon from '@mui/icons-material/Dashboard';
import PaidIcon from '@mui/icons-material/Paid';
import BarChartIcon from '@mui/icons-material/BarChart';
import PeopleIcon from '@mui/icons-material/People';
import LoginIcon from '@mui/icons-material/Login';
import LogoutIcon from '@mui/icons-material/Logout';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import { useSelector, useDispatch } from 'react-redux';
import { logout as logoutAction } from '../slices/authSlice';
import { useLanguage } from '../contexts/LanguageContext';
import LanguageSwitcherV2 from './LanguageSwitcherV2';

const PublicHeader = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { language, changeLanguage, t } = useLanguage();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  // Obtener estado de autenticación de Redux
  const { token, user } = useSelector(state => state.auth);

  // Manejar el cierre de sesión
  const handleLogout = () => {
    dispatch(logoutAction());
    navigate('/');
  };

  // Función que devuelve el tooltip correcto según el idioma
  const getConsultantTooltip = () => {
    if (language === 'es') {
      return "Habla con nuestro consultor financiero";
    } else {
      return "Talk to our financial consultant";
    }
  };
  const location = useLocation();
  const isLoginPage = location.pathname === '/login';

  // Menu states
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [anchorElProducts, setAnchorElProducts] = useState(null);
  const [anchorElResources, setAnchorElResources] = useState(null);
  const [anchorElLanguage, setAnchorElLanguage] = useState(null);

  // Menu handlers
  const handleProductsOpen = (event) => setAnchorElProducts(event.currentTarget);
  const handleProductsClose = () => setAnchorElProducts(null);

  const handleResourcesOpen = (event) => setAnchorElResources(event.currentTarget);
  const handleResourcesClose = () => setAnchorElResources(null);

  const handleLanguageOpen = (event) => setAnchorElLanguage(event.currentTarget);
  const handleLanguageClose = () => setAnchorElLanguage(null);

  const handleLanguageChange = (languageCode) => {
    // Change the language in the context
    changeLanguage(languageCode);

    // Force a reload to ensure all translated content updates
    // This is more reliable than trying to re-render all components
    window.location.reload();

    handleLanguageClose();
  };

  const toggleDrawer = (open) => (event) => {
    if (event.type === 'keydown' && (event.key === 'Tab' || event.key === 'Shift')) {
      return;
    }
    setDrawerOpen(open);
  };

  // Navigation items
  const navigationItems = [
    {
      label: language === 'es' ? 'Características' : 'Features',
      path: '/features'
    },
    {
      label: language === 'es' ? 'Precios' : 'Pricing',
      path: '/precios'
    },
    // 12 ago 2026 (pedido de Augusto): el dropdown "Recursos" (Guías/Blog/
    // Soporte) se sacó del sitio público -- esas páginas ahora viven
    // dentro de la app, en una sección propia del sidebar (ver
    // NotionSidebar.js), pensadas para el cliente que ya se registró, no
    // para un visitante del sitio de marketing.
    {
      label: language === 'es' ? 'Nosotros' : 'About',
      path: '/nosotros'
    }
  ];

  // Available languages
  const languages = [
    { code: 'en', label: 'English' },
    { code: 'es', label: 'Español' },
    { code: 'pt', label: 'Português' }
  ];

  // Drawer content for mobile view
  const drawerContent = (
    <Box
      sx={{ width: 280 }}
      role="presentation"
    >
      <List>
        <ListItem component={RouterLink} to="/" onClick={toggleDrawer(false)}>
          <Typography variant="h6" color="primary" fontWeight="bold">
            PCM Finance
          </Typography>
        </ListItem>
        <Divider />

        {navigationItems.map((item) => (
          <React.Fragment key={item.label}>
            {item.hasSubmenu ? (
              <>
                <ListItem>
                  <ListItemText primary={item.label} />
                </ListItem>
                <List component="div" disablePadding>
                  {item.submenuItems.map((subItem) => (
                    <ListItem
                      key={subItem.label}
                      component={RouterLink}
                      to={subItem.path}
                      onClick={toggleDrawer(false)}
                      sx={{ pl: 4 }}
                    >
                      <ListItemText
                        primary={subItem.label}
                        secondary={subItem.description}
                      />
                    </ListItem>
                  ))}
                </List>
              </>
            ) : (
              <ListItem
                component={RouterLink}
                to={item.path}
                onClick={toggleDrawer(false)}
              >
                <ListItemText primary={item.label} />
              </ListItem>
            )}
          </React.Fragment>
        ))}

        <Divider />

        {/* El selector de idioma se ha eliminado de aquí para evitar duplicaciones.
           Solo se mantiene el selector en la barra superior. */}

        <Divider />

        <ListItem
          component={RouterLink}
          to="/consultant"
          onClick={toggleDrawer(false)}
          sx={{ '&:hover': { backgroundColor: 'rgba(76, 175, 80, 0.08)' } }}
        >
          <ListItemIcon>
            <PeopleIcon sx={{ color: 'success.main' }} />
          </ListItemIcon>
          <ListItemText
            primary={<Typography color="success.main" fontWeight="medium">
              {language === 'es' ? "Asesoría" : "Consulting"}
            </Typography>}
            secondary={language === 'es' ? "Habla con nuestro consultor financiero" : "Talk to our financial consultant"}
          />
        </ListItem>

        {!isLoginPage && (
          <ListItem
            component={RouterLink}
            to="/register"
            onClick={toggleDrawer(false)}
            sx={{ '&:hover': { backgroundColor: 'primary.light' } }}
          >
            <ListItemIcon>
              <PersonAddIcon color="primary" />
            </ListItemIcon>
            <ListItemText
              primary={<Typography color="primary" fontWeight="bold">
                {language === 'es' ? "CREAR CUENTA" : "CREATE ACCOUNT"}
              </Typography>}
            />
          </ListItem>
        )}

        {!isLoginPage && (
          <ListItem
            component={RouterLink}
            to="/login?from=portal"
            onClick={toggleDrawer(false)}
          >
            <ListItemIcon>
              <LoginIcon />
            </ListItemIcon>
            <ListItemText
              primary={language === 'es' ? "INGRESO CLIENTES" : "CLIENT PORTAL"}
            />
          </ListItem>
        )}
      </List>
    </Box>
  );

  return (
    <AppBar position="fixed" color="default" elevation={1} sx={{ bgcolor: 'rgba(255, 255, 255, 0.95)', backdropFilter: 'blur(8px)' }}>
      <Container maxWidth="lg">
        <Toolbar disableGutters sx={{ justifyContent: 'space-between' }}>
          {/* Logo */}
          <Typography
            variant="h6"
            component={RouterLink}
            to="/"
            sx={{
              fontWeight: 700,
              color: 'primary.main',
              textDecoration: 'none',
              display: 'flex',
              alignItems: 'center',
              whiteSpace: 'nowrap',
              flexShrink: 0,
            }}
          >
            <PaidIcon sx={{ mr: 1, color: goldColors.main }} />
            {language === 'es' ? 'PCM Finanzas' : 'PCM Finance'}
          </Typography>

          {/* Desktop Navigation */}
          {!isMobile && (
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              {navigationItems.map((item) => (
                <Box key={item.label} sx={{ mx: 0.5 }}>
                  {item.hasSubmenu ? (
                    <React.Fragment>
                      <Button
                        color="inherit"
                        aria-haspopup="true"
                        onClick={item.label === 'Products' ? handleProductsOpen : handleResourcesOpen}
                        endIcon={<KeyboardArrowDownIcon sx={{ color: goldColors.main }} />}
                      >
                        {item.label}
                      </Button>
                      <Menu
                        anchorEl={item.label === 'Products' ? anchorElProducts : anchorElResources}
                        open={Boolean(item.label === 'Products' ? anchorElProducts : anchorElResources)}
                        onClose={item.label === 'Products' ? handleProductsClose : handleResourcesClose}
                        MenuListProps={{
                          'aria-labelledby': 'products-button',
                        }}
                        sx={{ mt: 1 }}
                      >
                        {item.submenuItems.map((subItem) => (
                          <MenuItem
                            key={subItem.label}
                            component={RouterLink}
                            to={subItem.path}
                            onClick={item.label === 'Products' ? handleProductsClose : handleResourcesClose}
                            sx={{
                              width: 220,
                              py: 1
                            }}
                          >
                            <Box>
                              <Typography variant="body1">{subItem.label}</Typography>
                              <Typography variant="caption" color="text.secondary">{subItem.description}</Typography>
                            </Box>
                          </MenuItem>
                        ))}
                      </Menu>
                    </React.Fragment>
                  ) : (
                    <Button
                      component={RouterLink}
                      to={item.path}
                      color="inherit"
                      sx={{ whiteSpace: 'nowrap', minWidth: 'auto' }}
                    >
                      {item.label}
                    </Button>
                  )}
                </Box>
              ))}
            </Box>
          )}

          {/* Right Side Actions */}
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            {/* Language Selector */}
            {!isMobile && (
              <Box sx={{ mx: 1 }}>
                <LanguageSwitcherV2 size="small" />
              </Box>
            )}

            {/* Consultant Button */}
            {/* Antes tenía un tooltip hover con texto hardcodeado en inglés
                ("Your first consultation is FREE!") que no es cierto — la
                Asesoría Personal cuesta $45/sesión (ver /precios) y ahora se
                agenda con confirmación manual en Cal.com. Se quitó el
                tooltip por completo; el botón ya es autoexplicativo. */}
            {!isMobile && (
              <Box sx={{ position: 'relative' }}>
                  <Button
                    variant="outlined"
                    color="success"
                    startIcon={<PeopleIcon sx={{ color: goldColors.main }} />}
                    component={RouterLink}
                    to="/consultant"
                    size="small"
                    sx={{
                      ml: 1.5,
                      borderWidth: 2,
                      borderRadius: '20px',
                      fontWeight: 'bold',
                      px: 1.5,
                      whiteSpace: 'nowrap',
                      '&:hover': {
                        backgroundColor: 'success.main',
                        color: 'white',
                        transform: 'translateY(-2px)',
                        transition: 'all 0.3s'
                      }
                    }}
                  >
                    {language === 'es' ? "Asesoría" : "Consulting"}
                  </Button>
              </Box>
            )}

            {/* Portal Button */}
            {!isMobile && (
              <>
                {token ? (
                  <Button
                    variant="outlined"
                    color="error"
                    startIcon={<LogoutIcon />}
                    onClick={handleLogout}
                    sx={{ ml: 1.5, whiteSpace: 'nowrap' }}
                  >
                    {language === 'es' ? "SALIR" : "LOGOUT"}
                  </Button>
                ) : (
                  <>
                    {/* 3 ago 2026 (pedido de Augusto): un cliente que ya
                        tiene cuenta y llega a /login desde "Portal de
                        Clientes" o "Ingreso" no debería ver "Crear cuenta"
                        al lado -- confunde, como si tuviera que abrir una
                        cuenta nueva en vez de iniciar sesión con la que ya
                        tiene. Se oculta solo en /login; en el resto del
                        sitio público se sigue mostrando normalmente. */}
                    {!isLoginPage && (
                      <Button
                        variant="contained"
                        color="primary"
                        component={RouterLink}
                        to="/register"
                        size="small"
                        sx={{ ml: 1.5, fontWeight: 'bold', whiteSpace: 'nowrap', px: 1.5 }}
                      >
                        {language === 'es' ? "Crear cuenta" : "Create account"}
                      </Button>
                    )}
                    {/* 4 ago 2026 (pedido de Augusto): tampoco tiene sentido
                        mostrar el botón "Ingreso" en la propia página de
                        login -- el usuario ya está ahí. Mismo criterio que
                        "Crear cuenta" arriba (isLoginPage). */}
                    {!isLoginPage && (
                      <Button
                        variant="text"
                        color="inherit"
                        component={RouterLink}
                        to="/login?from=portal"
                        size="small"
                        sx={{ ml: 0.5, whiteSpace: 'nowrap', px: 1 }}
                      >
                        {language === 'es' ? "Ingreso" : "Log in"}
                      </Button>
                    )}
                  </>
                )}
              </>
            )}

            {/* Mobile Menu Toggle */}
            {isMobile && (
              <IconButton
                edge="end"
                color="inherit"
                aria-label="menu"
                onClick={toggleDrawer(true)}
              >
                <MenuIcon sx={{ color: goldColors.main }} />
              </IconButton>
            )}
          </Box>
        </Toolbar>
      </Container>

      {/* Mobile Drawer */}
      <Drawer
        anchor="right"
        open={drawerOpen}
        onClose={toggleDrawer(false)}
      >
        {drawerContent}
      </Drawer>
    </AppBar>
  );
};

export default PublicHeader;
